<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="open"
        ref="container"
        class="fixed inset-0 z-[400] flex cursor-pointer items-center justify-center bg-[rgba(11,18,32,0.6)] p-4"
        @click="onOverlayClick"
        @keydown="onKeydown"
      >
        <div
          ref="panel"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="description ? descId : undefined"
          tabindex="-1"
          :class="panelClasses"
        >
          <header class="mb-4 flex items-start justify-between gap-4">
            <div class="min-w-0">
              <h2 :id="titleId" class="text-xl font-bold text-ink-strong">{{ title }}</h2>
              <p v-if="description" :id="descId" class="mt-1 text-sm text-ink-muted">
                {{ description }}
              </p>
            </div>
            <BaseIconButton
              v-if="dismissible"
              size="sm"
              variant="ghost"
              :label="ariaLabelClose ?? t('aria.closeModal')"
              @click="close"
            >
              <BaseIcon name="close" :size="20" />
            </BaseIconButton>
          </header>
          <div class="text-ink">
            <slot />
          </div>
          <footer v-if="$slots.footer" class="mt-6 flex flex-wrap justify-end gap-3">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { cva, type VariantProps } from 'class-variance-authority';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseIconButton from '@/components/base/BaseIconButton.vue';
import { useFocusTrap } from '@/composables/useFocusTrap';
import { useScrollLock } from '@/composables/useScrollLock';
import { useId } from '@/composables/useId';

const modalPanelVariants = cva('w-full rounded-2xl bg-paper p-6 shadow-xl outline-none', {
  variants: {
    size: {
      sm: 'max-w-[400px]',
      md: 'max-w-[520px]',
      lg: 'max-w-[720px]',
    },
  },
  defaultVariants: { size: 'md' },
});

export type ModalSize = NonNullable<VariantProps<typeof modalPanelVariants>['size']>;

interface Props {
  open?: boolean;
  title: string;
  description?: string;
  size?: ModalSize;
  closeOnOverlayClick?: boolean;
  dismissible?: boolean;
  ariaLabelClose?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  open: false,
  description: '',
  size: 'md',
  closeOnOverlayClick: true,
  dismissible: true,
  ariaLabelClose: null,
});

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'close'): void;
}>();

const { t } = useI18n();

const panel = ref<HTMLElement | null>(null);
const container = ref<HTMLElement | null>(null);
const openState = computed(() => props.open);

const titleId = useId('modal-title');
const descId = useId('modal-desc');

useFocusTrap(panel, openState);
const scrollLock = useScrollLock();

function close(): void {
  if (!props.dismissible) return;
  emit('update:open', false);
  emit('close');
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.dismissible) {
    event.preventDefault();
    close();
  }
}

function onOverlayClick(event: MouseEvent): void {
  if (!props.closeOnOverlayClick) return;
  if (event.target === container.value) close();
}

let locked = false;
watch(
  () => props.open,
  async (value) => {
    if (value && !locked) {
      scrollLock.lock();
      locked = true;
      await nextTick();
    } else if (!value && locked) {
      scrollLock.unlock();
      locked = false;
    }
  },
  { immediate: true },
);

const panelClasses = computed(() => modalPanelVariants({ size: props.size }));
</script>
